import React, { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { AlertTriangle, CheckCircle2, Loader2, Mail, Sparkles } from "lucide-react";
import api from "../utils/api";
import { ROUTES, API_ENDPOINTS } from "../config/constants";

const UUID_PATTERN =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const INVALID_LINK_MESSAGE =
    "This unsubscribe link is invalid. Please use the link from your most recent email or contact the sender directly.";

const NETWORK_ERROR_MESSAGE =
    "We could not reach the unsubscribe service. Please try again in a moment.";

const isUuid = (value) => typeof value === "string" && UUID_PATTERN.test(value);

const UnsubscribePage = () => {
    const { orgId, leadId } = useParams();
    const fetchStartedRef = useRef(false);

    const [state, setState] = useState("loading"); // "loading" | "success" | "already" | "error" | "invalid"
    const [message, setMessage] = useState("");
    const [errorDetail, setErrorDetail] = useState("");
    const [isRetrying, setIsRetrying] = useState(false);

    const hasValidIds = isUuid(orgId) && isUuid(leadId);

    const fetchUnsubscribe = React.useCallback(async () => {
        setState("loading");
        setMessage("");
        setErrorDetail("");

        try {
            const response = await api.get(
                API_ENDPOINTS.UNSUBSCRIBE(orgId, leadId),
            );

            const payload = response.data || {};
            const status = payload.status;

            if (status === "already_unsubscribed") {
                setState("already");
                setMessage(
                    payload.message ||
                        "You are already unsubscribed. No further emails will be sent.",
                );
                return;
            }

            setState("success");
            setMessage(
                payload.message ||
                    "You have been unsubscribed. You will not receive any further emails from this sender.",
            );
        } catch (error) {
            const status = error?.response?.status;

            // Anti-phishing posture from the BE: even malformed links must
            // never produce a 404 to the email client. The BE returns 422
            // for invalid UUIDs; we surface that as "invalid" with a retry
            // path to nothing (the link can't be fixed).
            if (status === 422) {
                setState("invalid");
                setErrorDetail("invalid-uuid-format");
                return;
            }

            // The BE catches its own DB errors and returns 200, so anything
            // that reaches this catch is network / 5xx — show the network
            // error and offer a retry.
            setState("error");
            setMessage(NETWORK_ERROR_MESSAGE);
            setErrorDetail(
                error?.message || `HTTP ${status || "unknown"}`,
            );
        }
    }, [orgId, leadId]);

    useEffect(() => {
        if (!hasValidIds) {
            setState("invalid");
            return;
        }

        // Guard against React 18 StrictMode double-invocation in dev.
        if (fetchStartedRef.current) return;
        fetchStartedRef.current = true;

        fetchUnsubscribe();
    }, [hasValidIds, fetchUnsubscribe]);

    const handleRetry = async () => {
        setIsRetrying(true);
        try {
            await fetchUnsubscribe();
        } finally {
            setIsRetrying(false);
        }
    };

    const isLoading = state === "loading";
    const isSuccess = state === "success";
    const isAlready = state === "already";
    const isError = state === "error";
    const isInvalid = state === "invalid";

    return (
        <div className="flex min-h-[calc(100vh-64px)] items-center justify-center bg-[#F8F8FE] px-3 py-8 sm:px-6 lg:px-8">
            <div className="grid w-full max-w-5xl overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm lg:grid-cols-[0.95fr_1.05fr]">
                <div className="hidden bg-gradient-to-br from-indigo-600 via-indigo-500 to-violet-500 p-8 text-white lg:flex lg:flex-col lg:justify-between">
                    <div>
                        <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-white/15 ring-1 ring-white/20">
                            <Sparkles size={24} aria-hidden="true" />
                        </div>
                        <h1 className="mt-8 text-3xl font-bold leading-tight">
                            {isSuccess || isAlready
                                ? "You are unsubscribed"
                                : "Unsubscribing…"}
                        </h1>
                        <p className="mt-4 max-w-sm text-sm leading-6 text-indigo-50">
                            We respect your inbox. It usually takes less than
                            48 hours for the change to take effect across all
                            in-flight campaigns.
                        </p>
                    </div>
                    <p className="text-xs font-medium uppercase tracking-[0.2em] text-indigo-100">
                        OptiMind unsubscribe
                    </p>
                </div>

                <div className="w-full p-5 sm:p-8 lg:p-10">
                    <div className="mx-auto max-w-md">
                        <div className="mb-6 text-center sm:mb-8">
                            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-600 lg:hidden">
                                {isSuccess || isAlready ? (
                                    <CheckCircle2 size={24} aria-hidden="true" />
                                ) : isError || isInvalid ? (
                                    <AlertTriangle size={24} aria-hidden="true" />
                                ) : (
                                    <Mail size={24} aria-hidden="true" />
                                )}
                            </div>
                            <p className="text-sm font-medium text-indigo-600">
                                Email preferences
                            </p>
                            <h2 className="mt-2 text-2xl font-bold text-slate-900 sm:text-3xl">
                                {isSuccess
                                    ? "Unsubscribed"
                                    : isAlready
                                      ? "Already unsubscribed"
                                      : isError
                                        ? "Something went wrong"
                                        : isInvalid
                                          ? "Invalid unsubscribe link"
                                          : "Unsubscribing…"}
                            </h2>
                        </div>

                        {isLoading && (
                            <div
                                className="flex flex-col items-center gap-4 py-6"
                                aria-live="polite"
                                aria-busy="true"
                            >
                                <Loader2
                                    size={40}
                                    className="animate-spin text-indigo-600"
                                    aria-hidden="true"
                                />
                                <p className="text-sm font-medium text-slate-600">
                                    Recording your unsubscribe request…
                                </p>
                            </div>
                        )}

                        {(isSuccess || isAlready) && (
                            <div className="space-y-5" aria-live="polite">
                                <div className="flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm leading-6 text-emerald-800">
                                    <CheckCircle2
                                        size={20}
                                        className="mt-0.5 shrink-0 text-emerald-600"
                                        aria-hidden="true"
                                    />
                                    <p>{message}</p>
                                </div>
                                <p className="text-xs text-slate-500">
                                    Reference: lead_id={leadId}
                                </p>
                                <Link
                                    to={ROUTES.LANDING_PAGE}
                                    className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-indigo-600 px-4 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700"
                                >
                                    Return to OptiMind home
                                </Link>
                            </div>
                        )}

                        {isError && (
                            <div className="space-y-4" aria-live="assertive">
                                <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium leading-6 text-red-800">
                                    {message}
                                </div>
                                {errorDetail && (
                                    <p className="text-xs text-slate-500">
                                        Detail: {errorDetail}
                                    </p>
                                )}
                                <button
                                    type="button"
                                    onClick={handleRetry}
                                    disabled={isRetrying}
                                    className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-indigo-600 px-4 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-indigo-400"
                                >
                                    {isRetrying ? (
                                        <Loader2
                                            size={18}
                                            className="animate-spin"
                                            aria-hidden="true"
                                        />
                                    ) : null}
                                    {isRetrying ? "Retrying…" : "Try again"}
                                </button>
                            </div>
                        )}

                        {isInvalid && (
                            <div className="space-y-4" aria-live="polite">
                                <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-medium leading-6 text-amber-800">
                                    {INVALID_LINK_MESSAGE}
                                </div>
                                <Link
                                    to={ROUTES.LANDING_PAGE}
                                    className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-indigo-600 px-4 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700"
                                >
                                    Return to OptiMind home
                                </Link>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UnsubscribePage;